import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';

export const AwardsSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(10, 0),
    background: theme.palette.mode === 'dark'
        ? 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.6) 100%)'
        : 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.05) 100%)',
    position: 'relative',
    overflow: 'hidden',
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: theme.palette.mode === 'dark'
            ? 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0) 70%)'
            : 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)',
        pointerEvents: 'none',
    }
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    marginBottom: theme.spacing(8),
    color: theme.palette.primary.main,
    textAlign: 'center',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    '&:after': {
        content: '""',
        position: 'absolute',
        bottom: '-20px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '60px',
        height: '4px',
        background: theme.palette.primary.main,
        borderRadius: '2px',
    }
}));

export const AwardCard = styled(motion.div)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: '20px',
    background: theme.palette.mode === 'dark'
        ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)'
        : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    border: `1px solid ${theme.palette.mode === 'dark'
        ? 'rgba(111, 24, 24, 0.08)'
        : 'rgba(0, 0, 0, 0.1)'}`,
    boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 30px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.07)'
        : '0 4px 20px rgba(0, 0, 0, 0.08)',
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    perspective: '1000px',
    transformStyle: 'preserve-3d',
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: theme.palette.mode === 'dark'
            ? 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(255,255,255,0.1) 50%, rgba(0,0,0,0) 100%)'
            : 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0) 100%)',
        transition: 'transform 0.3s ease-in-out',
        transform: 'translateX(-100%)',
    },
    '&:hover': {
        '&:before': {
            transform: 'translateX(100%)',
        }
    },
}));

export const AwardIcon = styled(Box)(({ theme }) => ({
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
    background: theme.palette.mode === 'dark'
        ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)'
        : 'linear-gradient(145deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.04) 100%)',
    border: `1px solid ${theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.15)'}`,
    boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1)'
        : '0 4px 15px rgba(0, 0, 0, 0.1)',
    position: 'relative',
    '&:before': {
        content: '""',
        position: 'absolute',
        top: '-2px',
        left: '-2px',
        right: '-2px',
        bottom: '-2px',
        borderRadius: '50%',
        background: theme.palette.mode === 'dark'
            ? 'conic-gradient(from 0deg, transparent, rgba(255,255,255,0.3), transparent)'
            : 'conic-gradient(from 0deg, transparent, rgba(25,118,210,0.4), transparent)',
        animation: 'rotate 3s linear infinite',
        zIndex: 0
    },
    '&:after': {
        content: '""',
        position: 'absolute',
        top: '-2px',
        left: '-2px',
        right: '-2px',
        bottom: '-2px',
        borderRadius: '50%',
        background: theme.palette.mode === 'dark'
            ? 'conic-gradient(from 180deg, transparent, rgba(255,255,255,0.3), transparent)'
            : 'conic-gradient(from 180deg, transparent, rgba(25,118,210,0.4), transparent)',
        animation: 'rotateReverse 3s linear infinite',
        zIndex: 0
    },
    '@keyframes rotate': {
        '0%': {
            transform: 'rotate(0deg)',
        },
        '100%': {
            transform: 'rotate(360deg)',
        },
    },
    '@keyframes rotateReverse': {
        '0%': {
            transform: 'rotate(360deg)',
        },
        '100%': {
            transform: 'rotate(0deg)',
        },
    },
    '& svg': {
        fontSize: '3.5rem',
        color: theme.palette.primary.main,
        filter: 'drop-shadow(0 0 8px rgba(0,0,0,0.3))',
        position: 'relative',
        zIndex: 1,
        background: theme.palette.mode === 'dark'
            ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%)'
            : 'linear-gradient(145deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.85) 100%)',
        padding: '12px',
        borderRadius: '50%',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 20px rgba(0, 0, 0, 0.3)'
            : '0 4px 15px rgba(0, 0, 0, 0.1)',
    }
}));

export const AwardYear = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
}));

export const YearBadge = styled(Typography)(({ theme }) => ({
    background: theme.palette.background.paper,
    padding: '4px 12px',
    borderRadius: '16px',
    color: theme.palette.text.primary,
    fontWeight: 500,
    boxShadow: theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(0, 0, 0, 0.2)'
        : '0 2px 8px rgba(0, 0, 0, 0.1)'
}));

export const FrequencyBadge = styled(Typography)(({ theme }) => ({
    background: '#1A73E8',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: '16px',
    fontSize: '0.875rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    cursor: 'help',
    boxShadow: '0 2px 8px rgba(26, 115, 232, 0.3)',
    '&:hover': {
        background: '#1557B0'
    }
})); 